import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Chapter, FadeUp } from "./Reveal";
import { WHATSAPP_LINK } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FinalCta = () => {
    const [form, setForm] = useState({ name: "", business: "", message: "" });
    const [status, setStatus] = useState("idle");

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.message.trim()) {
            toast.error("Please add your name and a short message.");
            return;
        }
        setStatus("sending");
        try {
            await axios.post(`${API}/contact`, form);
            setStatus("sent");
            toast.success("Message received. We'll get back to you shortly.");
        } catch (err) {
            setStatus("idle");
            toast.error("Something went wrong. Please try WhatsApp instead.");
        }
    };

    const inputCls =
        "w-full rounded-md border border-white/15 bg-white/5 px-4 py-3.5 text-white placeholder:text-white/40 text-sm md:text-base focus:outline-none focus:border-accent transition-colors duration-200";

    return (
        <section id="contact" className="relative bg-navy py-24 md:py-32 overflow-hidden grain" data-testid="contact-section">
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[140px] pointer-events-none" aria-hidden="true" />
            <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
                <Chapter
                    dark
                    index="07"
                    label="Contact"
                    title={["Ready when", "you are."]}
                    note="Tell us where you want to grow. The fastest reply is always WhatsApp. The form works too."
                />

                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
                    <FadeUp>
                        <p className="font-display text-2xl md:text-3xl font-medium leading-snug tracking-tight text-white max-w-md">
                            One conversation is usually enough to know if we can move your numbers.
                        </p>
                        <motion.a
                            href={WHATSAPP_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-cta"
                            whileTap={{ scale: 0.96 }}
                            className="mt-10 inline-flex items-center gap-3 rounded-full bg-accent px-9 py-4.5 py-4 text-base md:text-lg font-semibold text-white hover:bg-white hover:text-navy transition-colors duration-300"
                        >
                            Chat on WhatsApp
                            <ArrowUpRight className="w-5 h-5" />
                        </motion.a>
                        <p className="mt-6 text-sm text-white/50">
                            WhatsApp +91 86999 49915 · Call +91 97799 97673 · admin@chatproz.com · Ludhiana, Punjab, India
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.15}>
                        {status === "sent" ? (
                            <div
                                className="rounded-lg border border-white/15 bg-white/5 p-10 text-center"
                                data-testid="contact-form-success"
                            >
                                <CheckCircle2 className="w-12 h-12 text-accent mx-auto mb-5" />
                                <p className="font-display text-2xl font-semibold text-white">Message received.</p>
                                <p className="mt-3 text-white/60 text-sm md:text-base">
                                    We'll get back to you shortly. In a hurry?
                                </p>
                                <a
                                    href={WHATSAPP_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-success-whatsapp-link"
                                    className="mt-2 inline-block text-accent font-semibold text-sm border-b border-accent/40 pb-0.5"
                                >
                                    Chat on WhatsApp
                                </a>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                                <input
                                    type="text"
                                    placeholder="Your name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className={inputCls}
                                    data-testid="contact-form-name"
                                    required
                                />
                                <input
                                    type="text"
                                    placeholder="Your business"
                                    value={form.business}
                                    onChange={(e) => setForm({ ...form, business: e.target.value })}
                                    className={inputCls}
                                    data-testid="contact-form-business"
                                />
                                <textarea
                                    placeholder="What do you want to grow?"
                                    rows={4}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className={`${inputCls} resize-none`}
                                    data-testid="contact-form-message"
                                    required
                                />
                                <motion.button
                                    type="submit"
                                    disabled={status === "sending"}
                                    whileTap={{ scale: 0.97 }}
                                    data-testid="contact-form-submit"
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-navy hover:bg-accent hover:text-white transition-colors duration-300 disabled:opacity-60"
                                >
                                    {status === "sending" ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send message
                                            <ArrowUpRight className="w-5 h-5" />
                                        </>
                                    )}
                                </motion.button>
                            </form>
                        )}
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};

export default FinalCta;
